import { ChevronDown, ChevronUp, MoreHorizontal, X } from "lucide-react";
import { Link } from "react-router";

import { buildSessionUrl } from "@/components/pages_logged_in/roleplays/utils";
import { RankIcon } from "@/components/pieces/roleplays/RankIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { RoleplayRank, ScenarioDifficulty, ScenarioType } from "@/graphql";
import { formatDate } from "@/utils/datetime";

import { SimpleScenarioCard } from "./AssignScenarioDialog";
import { ProgressionChart } from "./ProgressionChart";

interface SessionHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleplaySlug?: string;
}

// Mock session data type
interface MockSession {
  uuid: string;
  updatedAt: string;
  rank: RoleplayRank;
  score: number | null;
  scenario: {
    slug: string;
  };
}

// Mock data for the assignment details - replace with actual data from your backend
const mockAssignmentData = {
  participant: {
    name: "Jason Briggs",
    avatar:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
  },
  assignedBy: "Alyssa Smith",
  assignedOn: "Apr 1, 2025",
  completed: "Apr 2, 2025",
  dueDate: "Apr 15, 2025",
  status: "Complete",
  scenario: {
    id: "scenario-1",
    name: "The Budget Slasher",
    shortDesc:
      "Deliver feedback to talented employee who is missing deadlines.",
    difficulty: ScenarioDifficulty.Easy,
    minDuration: 90,
    personaName: "Female 1",
    personaPhotoUrl: "/images/ai-personalities/female-1.jpg",
    personaFirstName: "Sarah",
    personaLastName: "Johnson",
    personaJobTitle: "Senior Manager",
    personaCompany: "TechCorp",
    personaOpeningLine:
      "Hi, I'm Sarah. I've been reviewing your recent performance...",
    personaBackstory:
      "Sarah is a seasoned manager who needs to deliver difficult feedback...",
    personaSituationalUnderstanding:
      "Sarah understands the employee's value but must address performance issues...",
    personalityCharacteristics: "Direct, empathetic, results-oriented",
    personalityTraits: [],
    presentations: [],
    roleplaySessions: [],
    tags: [],
    slug: "budget-slasher",
    createdAt: "2025-01-01T00:00:00Z",
    objective:
      "Practice delivering constructive feedback to high-performing employees",
    context:
      "You are a manager who needs to address performance issues with a talented team member",
    rawObjective:
      "Practice delivering constructive feedback to high-performing employees",
    rawContext:
      "You are a manager who needs to address performance issues with a talented team member",
    introductionScript: "Welcome to this roleplay scenario...",
    introductionAudioUrl: null,
    voiceId: null,
    assistantId: null,
    canEdit: true,
    canShareToWorkspace: true,
    canUpdatePermission: true,
    maxGrantablePermissionLevel: null,
    owner: null,
    workspace: null,
    accessScope: null,
    sharingStatus: null,
    visibilityScope: null,
    status: "Active",
    numberOfSessions: 0,
    evaluationCriteria: [],
    collections: [],
    rank: RoleplayRank.Bronze,
    enableScreenSharing: false,
    autoShareSessions: false,
  } as ScenarioType,
  progression: {
    currentScore: 100,
    history: [
      { date: "Apr 4th at 9:00am", score: 94, rank: "gold" },
      { date: "Apr 3rd at 10:15am", score: 84, rank: "silver" },
      { date: "Apr 3rd at 9:10am", score: 76, rank: "bronze" },
      { date: "Apr 2nd at 1:30pm", score: 72, rank: "bronze" },
      { date: "Apr 2nd at 12:30pm", score: 58, rank: "unranked" },
    ],
  },
};

// Mock sessions data
const mockSessions: MockSession[] = [
  {
    uuid: "session-1",
    updatedAt: "2025-04-04T09:00:00Z",
    rank: RoleplayRank.Gold,
    score: 94,
    scenario: { slug: "budget-slasher" },
  },
  {
    uuid: "session-2",
    updatedAt: "2025-04-03T10:15:00Z",
    rank: RoleplayRank.Silver,
    score: 84,
    scenario: { slug: "budget-slasher" },
  },
  {
    uuid: "session-3",
    updatedAt: "2025-04-03T09:10:00Z",
    rank: RoleplayRank.Bronze,
    score: 76,
    scenario: { slug: "budget-slasher" },
  },
  {
    uuid: "session-4",
    updatedAt: "2025-04-02T13:30:00Z",
    rank: RoleplayRank.Bronze,
    score: 72,
    scenario: { slug: "budget-slasher" },
  },
  {
    uuid: "session-5",
    updatedAt: "2025-04-02T12:30:00Z",
    rank: RoleplayRank.Unranked,
    score: 58,
    scenario: { slug: "budget-slasher" },
  },
];

// Convert sessions to progression chart data
const progressionData = mockSessions.map((session, index) => ({
  session: mockSessions.length - index, // Reverse order so latest is on the right
  score: session.score || 0,
  date: formatDate(session.updatedAt, "MMM do 'at' h:mm a") || "",
  rank: session.rank.toLowerCase() as "gold" | "silver" | "bronze" | "unranked",
}));

export function AssignmentResultDrawer({
  open,
  onOpenChange,
  roleplaySlug,
}: SessionHistoryDrawerProps) {
  // Use mock sessions data
  const sessions: MockSession[] = mockSessions;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="max-w-lg!">
        <div className="flex flex-col h-full">
          {/* Header with close button and controls */}
          <div className="flex items-center justify-between p-3 absolute top-6 -left-12 rounded-md bg-white rounded-r-none">
            <div className="flex flex-col items-center gap-2">
              <div
                className="w-6 h-6 flex items-center justify-center cursor-pointer"
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                <X className="w-5 h-5" />
              </div>
              <div className="w-6 h-px bg-gray-300"></div>
              <div className="w-6 h-6  flex items-center justify-center cursor-pointer">
                <ChevronUp className="w-4 h-4" />
              </div>
              <div className="w-6 h-6 flex items-center justify-center cursor-pointer">
                <ChevronDown className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Participant and Menu */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-gray-200 rounded-full overflow-hidden">
                  <img
                    src={mockAssignmentData.participant.avatar}
                    alt={mockAssignmentData.participant.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
                <span className="font-semibold text-xl text-[#242424]">
                  {mockAssignmentData.participant.name}
                </span>
              </div>
              <Button
                variant="minimal"
                size="sm"
                className="w-10 h-10 p-0 border border-gray-200 rounded"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </div>

            {/* Assignment Details */}
            <Card className="p-6 border border-[#E2E2E2] bg-white">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[#778397] w-28">Assigned by:</span>
                  <span className="text-[#242424] font-medium">
                    {mockAssignmentData.assignedBy}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#778397] w-28">Assigned on:</span>
                  <span className="text-[#242424] font-medium">
                    {mockAssignmentData.assignedOn}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#778397] w-28">Completed:</span>
                  <span className="text-[#242424] font-medium">
                    {mockAssignmentData.completed}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#778397] w-28">Due Date:</span>
                  <span className="text-[#242424] font-medium">
                    {mockAssignmentData.dueDate}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#778397] w-28">Status:</span>
                  <Badge className="bg-[#ECFAF3] text-[#0D8050] border-0 px-2 py-1">
                    <div className="w-4 h-4 rounded-full bg-[#0D8050] flex items-center justify-center mr-2">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    </div>
                    {mockAssignmentData.status}
                  </Badge>
                </div>
              </div>
            </Card>

            {/* Scenario Section */}
            <div className="space-y-3">
              <h3 className="font-semibold text-base text-black">Scenario</h3>
              <SimpleScenarioCard
                scenario={mockAssignmentData.scenario}
                selected={false}
                onToggle={() => {}}
                selectable={false}
              />
            </div>

            {/* Progression Section */}
            <div className="space-y-3">
              <h3 className="font-semibold text-base text-black">
                Progression
              </h3>
              <Card className="p-6 border border-[#E2E2E2] bg-white">
                <ProgressionChart data={progressionData} />
              </Card>
            </div>

            {/* History Section */}
            <div className="space-y-3">
              <h3 className="font-semibold text-base text-black">History</h3>
              <div className="space-y-3">
                {sessions.length === 0 ? (
                  <Card className="p-8 text-center text-gray-500">
                    <p className="text-lg">No sessions... yet!</p>
                    <p className="text-sm mt-2">
                      Start a new session to see your history here.
                    </p>
                  </Card>
                ) : (
                  sessions.map((session) => {
                    if (!session) return null;
                    const url = buildSessionUrl({
                      roleplaySlug,
                      scenarioSlug: session.scenario.slug,
                      sessionUuid: session.uuid,
                    });

                    return (
                      <Link
                        key={session.uuid}
                        to={url}
                        className="block transition-colors duration-200 text-gray-900 hover:text-inherit"
                      >
                        <Card className="p-4 border border-[#E2E2E2] bg-white hover:border-blue-700 transition-colors">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-[#242424]">
                              {formatDate(
                                session.updatedAt,
                                "MMM do 'at' h:mm a",
                              )}
                            </span>
                            <div className="flex items-center gap-2">
                              <RankIcon rank={session.rank} size={24} />
                              {session.score ? (
                                <span className="text-sm text-[#242424]">
                                  {session.score}%
                                </span>
                              ) : (
                                <span className="text-sm text-[#242424]">
                                  Unranked
                                </span>
                              )}
                            </div>
                          </div>
                        </Card>
                      </Link>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
